/**
 * UnifiedPropertySearch - Consolidated Property Search Page
 * 
 * Merges functionality from:
 * - PropertySearch.js (basic search)
 * - PropertySearchV2.jsx (advanced features)
 * 
 * Features:
 * - Mobile-first responsive design
 * - Advanced filtering (price, bedrooms, property type, amenities)
 * - Grid/list/map view toggle
 * - Interactive Mapbox map with clustering and draw-to-search
 * - URL-based filter state for shareable links
 * - Lazy loading / pagination
 * - Saved searches for authenticated users
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import SEO, { SEOConfig } from '../components/SEO';
import { 
  Search, 
  SlidersHorizontal, 
  Grid, 
  List, 
  MapPin, 
  Bed, 
  Bath, 
  Home,
  Heart,
  ChevronDown,
  ChevronUp,
  X,
  RefreshCw,
  AlertCircle,
  ArrowUpDown,
  Ruler,
  PoundSterling,
  Calendar,
  Check,
  Filter,
  Map,
  Save,
  Bell,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { cn, formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import PropertyMapView from '../components/property/PropertyMapView';

// =============================================================================
// CONSTANTS & CONFIG
// =============================================================================

const PROPERTY_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'flat', label: 'Flat/Apartment' },
  { value: 'house', label: 'House' },
  { value: 'studio', label: 'Studio' },
  { value: 'room', label: 'Room' },
  { value: 'hmo', label: 'HMO' },
  { value: 'bungalow', label: 'Bungalow' },
];

const BEDROOM_OPTIONS = [
  { value: '', label: 'Any' },
  { value: '0', label: 'Studio' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4+' },
];

const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'newest', label: 'Newest Listed' },
  { value: 'available_soonest', label: 'Available Soonest' },
];

const PRICE_PRESETS = [
  { min: 0, max: 500, label: 'Under £500' },
  { min: 500, max: 750, label: '£500 - £750' },
  { min: 750, max: 1000, label: '£750 - £1,000' },
  { min: 1000, max: 1500, label: '£1,000 - £1,500' },
  { min: 1500, max: 2000, label: '£1,500 - £2,000' },
  { min: 2000, max: null, label: '£2,000+' },
];

const AMENITY_FILTERS = [
  { key: 'pets_allowed', label: 'Pets Allowed', icon: '🐕' },
  { key: 'bills_included', label: 'Bills Included', icon: '💡' },
  { key: 'parking', label: 'Parking', icon: '🚗' },
  { key: 'garden', label: 'Garden', icon: '🌳' },
  { key: 'furnished', label: 'Furnished', icon: '🛋️' },
  { key: 'available_now', label: 'Available Now', icon: '✓' },
];


// =============================================================================
// FILTER PANEL COMPONENT
// =============================================================================

const FilterPanel = ({ filters, setFilters, onClose, isOpen, isMobile }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleApply = () => {
    setFilters(localFilters);
    onClose?.();
  };

  const handleReset = () => {
    const reset = {
      minPrice: '',
      maxPrice: '',
      bedrooms: '',
      propertyType: '',
      amenities: [],
    };
    setLocalFilters(reset);
    setFilters(reset);
  };

  const toggleAmenity = (key) => {
    setLocalFilters(prev => ({
      ...prev,
      amenities: prev.amenities?.includes(key)
        ? prev.amenities.filter(a => a !== key)
        : [...(prev.amenities || []), key],
    }));
  };

  const panelContent = (
    <div className="space-y-6 p-4">
      {/* Price Range */}
      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">Price Range (pcm)</label>
        <div className="flex gap-2 items-center mb-2">
          <div className="relative flex-1">
            <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="number"
              placeholder="Min"
              value={localFilters.minPrice || ''}
              onChange={(e) => setLocalFilters(prev => ({ ...prev, minPrice: e.target.value }))}
              className="pl-8"
              data-testid="filter-min-price"
            />
          </div>
          <span className="text-gray-400">-</span>
          <div className="relative flex-1">
            <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="number"
              placeholder="Max"
              value={localFilters.maxPrice || ''}
              onChange={(e) => setLocalFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
              className="pl-8"
              data-testid="filter-max-price"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {PRICE_PRESETS.map((preset, idx) => (
            <button
              key={idx}
              onClick={() => setLocalFilters(prev => ({
                ...prev,
                minPrice: preset.min || '',
                maxPrice: preset.max || '',
              }))}
              className={cn(
                "text-xs px-2 py-1 rounded-full border transition-colors",
                localFilters.minPrice == preset.min && localFilters.maxPrice == preset.max
                  ? "bg-blue-100 border-blue-300 text-blue-700"
                  : "hover:bg-gray-100"
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bedrooms */}
      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">Bedrooms</label>
        <div className="flex flex-wrap gap-2">
          {BEDROOM_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setLocalFilters(prev => ({ ...prev, bedrooms: opt.value }))}
              className={cn(
                "px-4 py-2 rounded-lg border text-sm transition-colors",
                localFilters.bedrooms === opt.value
                  ? "bg-blue-600 text-white border-blue-600"
                  : "hover:bg-gray-100"
              )}
              data-testid={`filter-bedroom-${opt.value || 'any'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Property Type */}
      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">Property Type</label>
        <div className="flex flex-wrap gap-2">
          {PROPERTY_TYPES.map((type) => (
            <button
              key={type.value}
              onClick={() => setLocalFilters(prev => ({ ...prev, propertyType: type.value }))}
              className={cn(
                "px-3 py-2 rounded-lg border text-sm transition-colors",
                localFilters.propertyType === type.value
                  ? "bg-blue-600 text-white border-blue-600"
                  : "hover:bg-gray-100"
              )}
              data-testid={`filter-type-${type.value || 'all'}`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      {/* Amenities */}
      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">Features</label>
        <div className="grid grid-cols-2 gap-2">
          {AMENITY_FILTERS.map((amenity) => (
            <button
              key={amenity.key}
              onClick={() => toggleAmenity(amenity.key)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors",
                localFilters.amenities?.includes(amenity.key)
                  ? "bg-blue-50 border-blue-300 text-blue-700"
                  : "hover:bg-gray-50"
              )}
              data-testid={`filter-amenity-${amenity.key}`}
            >
              <span>{amenity.icon}</span>
              <span>{amenity.label}</span>
              {localFilters.amenities?.includes(amenity.key) && (
                <Check className="w-4 h-4 ml-auto" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t">
        <Button variant="outline" onClick={handleReset} className="flex-1">
          Reset
        </Button>
        <Button onClick={handleApply} className="flex-1" data-testid="apply-filters">
          Apply Filters
        </Button>
      </div>
    </div>
  );

  // Mobile: Full-screen overlay
  if (isMobile && isOpen) {
    return (
      <div className="fixed inset-0 bg-white z-50 overflow-auto">
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex justify-between items-center">
          <h2 className="font-semibold text-lg">Filters</h2>
          <button onClick={onClose} className="p-2">
            <X className="w-5 h-5" />
          </button>
        </div>
        {panelContent}
      </div>
    );
  }

  // Desktop: Side panel
  if (!isMobile && isOpen) {
    return (
      <Card className="w-80 flex-shrink-0 self-start sticky top-4">
        <div className="px-4 py-3 border-b flex justify-between items-center">
          <h3 className="font-semibold">Filters</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        {panelContent}
      </Card>
    );
  }

  return null;
};


// =============================================================================
// PROPERTY CARD COMPONENT
// =============================================================================

const PropertyCard = ({ listing, viewMode, onFavorite, isFavorite }) => {
  const property = listing.property || {};
  const address = property.address || {};
  
  const imageUrl = listing.images?.[0]?.url || property.images?.[0]?.url || '/placeholder-property.jpg';
  const isAvailableNow = listing.available_from 
    ? new Date(listing.available_from) <= new Date() 
    : true;

  if (viewMode === 'list') {
    return (
      <Card className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`listing-${listing.id}`}>
        <div className="flex">
          <div className="w-48 h-36 flex-shrink-0">
            <img
              src={imageUrl}
              alt={listing.title || 'Property'}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = '/placeholder-property.jpg'; }}
            />
          </div>
          <CardContent className="flex-1 p-4">
            <div className="flex justify-between">
              <div className="flex-1">
                <Link to={`/properties/${listing.id}`}>
                  <h3 className="font-semibold text-lg hover:text-blue-600 transition-colors line-clamp-1">
                    {listing.title || `${property.bedrooms || 1} Bed ${property.property_type || 'Property'}`}
                  </h3>
                </Link>
                <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                  <MapPin className="w-4 h-4" />
                  {[address.city, address.postcode].filter(Boolean).join(', ') || 'Location TBD'}
                </p>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Bed className="w-4 h-4" />
                    {property.bedrooms || '-'} bed
                  </span>
                  <span className="flex items-center gap-1">
                    <Bath className="w-4 h-4" />
                    {property.bathrooms || '-'} bath
                  </span>
                  {property.sqft && (
                    <span className="flex items-center gap-1">
                      <Ruler className="w-4 h-4" />
                      {property.sqft} sq ft
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(listing.rent_amount || 0)}
                  <span className="text-sm text-gray-500 font-normal">/pcm</span>
                </p>
                <div className="flex gap-1 mt-2">
                  {isAvailableNow && <Badge className="bg-green-100 text-green-800">Available Now</Badge>}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t">
              <div className="flex gap-2">
                {listing.bills_included && <Badge variant="outline">Bills Inc.</Badge>}
                {listing.pets_allowed && <Badge variant="outline">Pets OK</Badge>}
                {property.parking && <Badge variant="outline">Parking</Badge>}
              </div>
              <button
                onClick={() => onFavorite?.(listing.id)}
                className={cn(
                  "p-2 rounded-full transition-colors",
                  isFavorite ? "text-red-500" : "text-gray-400 hover:text-red-500"
                )}
              >
                <Heart className={cn("w-5 h-5", isFavorite && "fill-current")} />
              </button>
            </div>
          </CardContent>
        </div>
      </Card>
    );
  }

  // Grid View
  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow group" data-testid={`listing-${listing.id}`}>
      <div className="relative h-48">
        <img
          src={imageUrl}
          alt={listing.title || 'Property'}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => { e.target.src = '/placeholder-property.jpg'; }}
        />
        <button
          onClick={() => onFavorite?.(listing.id)}
          className={cn(
            "absolute top-3 right-3 p-2 rounded-full bg-white/90 backdrop-blur-sm transition-colors",
            isFavorite ? "text-red-500" : "text-gray-600 hover:text-red-500"
          )}
        >
          <Heart className={cn("w-5 h-5", isFavorite && "fill-current")} />
        </button>
        {isAvailableNow && (
          <Badge className="absolute top-3 left-3 bg-green-500 text-white">Available Now</Badge>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
          <p className="text-white font-bold text-xl">
            {formatCurrency(listing.rent_amount || 0)}
            <span className="text-sm font-normal opacity-80">/pcm</span>
          </p>
        </div>
      </div>
      <CardContent className="p-4">
        <Link to={`/properties/${listing.id}`}>
          <h3 className="font-semibold hover:text-blue-600 transition-colors line-clamp-1">
            {listing.title || `${property.bedrooms || 1} Bed ${property.property_type || 'Property'}`}
          </h3>
        </Link>
        <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
          <MapPin className="w-3 h-3" />
          {[address.city, address.postcode].filter(Boolean).join(', ') || 'Location TBD'}
        </p>
        <div className="flex items-center gap-3 mt-3 text-sm text-gray-600">
          <span className="flex items-center gap-1">
            <Bed className="w-4 h-4" />
            {property.bedrooms || '-'}
          </span>
          <span className="flex items-center gap-1">
            <Bath className="w-4 h-4" />
            {property.bathrooms || '-'}
          </span>
          <span className="flex items-center gap-1">
            <Home className="w-4 h-4" />
            {property.property_type || 'Property'}
          </span>
        </div>
        <div className="flex flex-wrap gap-1 mt-3">
          {listing.bills_included && <Badge variant="outline" className="text-xs">Bills Inc.</Badge>}
          {listing.pets_allowed && <Badge variant="outline" className="text-xs">Pets OK</Badge>}
        </div>
      </CardContent>
    </Card>
  );
};


// =============================================================================
// SAVE SEARCH MODAL
// =============================================================================

const SaveSearchModal = ({ isOpen, onClose, searchCriteria, onSave }) => {
  const [name, setName] = useState('');
  const [emailAlerts, setEmailAlerts] = useState(false);
  const [pushAlerts, setPushAlerts] = useState(false);
  const [frequency, setFrequency] = useState('daily');
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name for this search');
      return;
    }

    setSaving(true);
    try {
      await api.post('/prs-services/saved-searches', {
        name: name.trim(),
        description: `Map search saved on ${new Date().toLocaleDateString()}`,
        search_criteria: searchCriteria,
        email_alerts: emailAlerts,
        push_alerts: pushAlerts,
        alert_frequency: frequency,
      });
      
      toast.success('Search saved successfully!');
      onSave?.();
      onClose();
    } catch (error) {
      console.error('Failed to save search:', error);
      toast.error('Failed to save search');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="font-semibold text-lg">Save This Search</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <CardContent className="p-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Search Name *</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., 2 bed flats in Manchester"
              data-testid="save-search-name"
            />
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 block">Notifications</label>
            
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={emailAlerts}
                onChange={(e) => setEmailAlerts(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">Email alerts for new matches</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={pushAlerts}
                onChange={(e) => setPushAlerts(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">Push notifications</span>
            </label>
          </div>

          {(emailAlerts || pushAlerts) && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Alert Frequency</label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                className="w-full h-10 px-3 border rounded-md text-sm"
              >
                <option value="instant">Instant</option>
                <option value="daily">Daily digest</option>
                <option value="weekly">Weekly digest</option>
              </select>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1">
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Search
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function UnifiedPropertySearch() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const searchInputRef = useRef(null);
  const mapRef = useRef(null);

  // State
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // UI State
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'grid');
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [pendingSearchCriteria, setPendingSearchCriteria] = useState(null);

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'recommended');
  const [filters, setFilters] = useState({
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    bedrooms: searchParams.get('bedrooms') || '',
    propertyType: searchParams.get('type') || '',
    amenities: searchParams.get('amenities')?.split(',').filter(Boolean) || [],
  });

  // Favorites
  const [favorites, setFavorites] = useState(new Set());

  // Responsive
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load listings
  const loadListings = useCallback(async (reset = false) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      if (filters.minPrice) params.set('min_rent', filters.minPrice);
      if (filters.maxPrice) params.set('max_rent', filters.maxPrice);
      if (filters.bedrooms) params.set('bedrooms', filters.bedrooms === '4' ? '4' : filters.bedrooms);
      if (filters.propertyType) params.set('property_type', filters.propertyType);
      if (filters.amenities?.length) {
        filters.amenities.forEach(a => params.append('amenities', a));
      }
      params.set('sort', sort);
      params.set('page', reset ? '1' : page.toString());
      params.set('limit', '20');

      const { data } = await api.get(`/listings/public/search?${params.toString()}`);
      
      const listingsData = Array.isArray(data) ? data : data.listings || [];
      const totalCount = data.total || listingsData.length;

      if (reset) {
        setListings(listingsData);
        setPage(1);
      } else {
        setListings(prev => [...prev, ...listingsData]);
      }

      setTotal(totalCount);
      setHasMore(listingsData.length >= 20);

    } catch (err) {
      console.error('Failed to load listings:', err);
      setError('Failed to load properties. Please try again.');
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filters.minPrice, filters.maxPrice, filters.bedrooms, filters.propertyType, filters.amenities, sort, page]);

  // Initial load (only for non-map view)
  useEffect(() => {
    if (viewMode !== 'map') {
      loadListings(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filters.minPrice, filters.maxPrice, filters.bedrooms, filters.propertyType, filters.amenities, sort, viewMode]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (filters.minPrice) params.set('minPrice', filters.minPrice);
    if (filters.maxPrice) params.set('maxPrice', filters.maxPrice);
    if (filters.bedrooms) params.set('bedrooms', filters.bedrooms);
    if (filters.propertyType) params.set('type', filters.propertyType);
    if (filters.amenities?.length) params.set('amenities', filters.amenities.join(','));
    if (sort !== 'recommended') params.set('sort', sort);
    if (viewMode !== 'grid') params.set('view', viewMode);
    
    setSearchParams(params, { replace: true });
  }, [searchQuery, filters, sort, viewMode, setSearchParams]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (viewMode === 'map' && mapRef.current) {
      // Map will handle its own search
    } else {
      loadListings(true);
    }
  };

  // Handle save search from map
  const handleSaveSearch = (criteria) => {
    setPendingSearchCriteria({
      search_text: searchQuery,
      property_type: filters.propertyType ? [filters.propertyType] : [],
      min_rent: filters.minPrice,
      max_rent: filters.maxPrice,
      min_bedrooms: filters.bedrooms,
      amenities: filters.amenities,
      ...criteria,
    });
    setShowSaveSearchModal(true);
  };

  // Handle favorite
  const handleFavorite = (id) => {
    if (!isAuthenticated) {
      toast.error('Please login to save favorites');
      navigate('/login');
      return;
    }

    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        toast.success('Removed from favorites');
      } else {
        next.add(id);
        toast.success('Added to favorites');
      }
      return next;
    });

    // TODO: Save to backend
  };

  // Load more
  const handleLoadMore = () => {
    if (!loading && hasMore) {
      setPage(p => p + 1);
      loadListings(false);
    }
  };

  // Active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.minPrice || filters.maxPrice) count++;
    if (filters.bedrooms) count++;
    if (filters.propertyType) count++;
    count += filters.amenities?.length || 0;
    return count;
  }, [filters]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="unified-property-search">
      <SEO {...SEOConfig.properties} />
      {/* Header with Back to Home */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Back to Home Link */}
          <div className="mb-3">
            <Link 
              to="/" 
              className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
              data-testid="back-to-home"
            >
              <Home className="w-4 h-4 mr-1" />
              Back to Home
            </Link>
          </div>
          
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search by city, postcode, or area..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" className="h-12 px-6" data-testid="search-button">
              Search
            </Button>
          </form>

          {/* Filter & Sort Bar */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={cn(activeFilterCount > 0 && "border-blue-500 text-blue-600")}
                data-testid="toggle-filters"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge className="ml-2 bg-blue-600 text-white">{activeFilterCount}</Badge>
                )}
              </Button>

              {/* Quick Filter Chips */}
              {!isMobile && (
                <div className="flex gap-2">
                  {filters.bedrooms && (
                    <Badge variant="secondary" className="gap-1">
                      {filters.bedrooms === '0' ? 'Studio' : `${filters.bedrooms} bed`}
                      <X 
                        className="w-3 h-3 cursor-pointer" 
                        onClick={() => setFilters(p => ({ ...p, bedrooms: '' }))}
                      />
                    </Badge>
                  )}
                  {filters.propertyType && (
                    <Badge variant="secondary" className="gap-1">
                      {PROPERTY_TYPES.find(t => t.value === filters.propertyType)?.label || filters.propertyType}
                      <X 
                        className="w-3 h-3 cursor-pointer" 
                        onClick={() => setFilters(p => ({ ...p, propertyType: '' }))}
                      />
                    </Badge>
                  )}
                  {(filters.minPrice || filters.maxPrice) && (
                    <Badge variant="secondary" className="gap-1">
                      {filters.minPrice && `£${filters.minPrice}`}
                      {filters.minPrice && filters.maxPrice && ' - '}
                      {filters.maxPrice && `£${filters.maxPrice}`}
                      <X 
                        className="w-3 h-3 cursor-pointer" 
                        onClick={() => setFilters(p => ({ ...p, minPrice: '', maxPrice: '' }))}
                      />
                    </Badge>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Sort (hidden in map view) */}
              {viewMode !== 'map' && (
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="h-10 px-3 border rounded-md text-sm bg-white"
                  data-testid="sort-select"
                >
                  {SORT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              )}

              {/* Save Search Button (for authenticated users) */}
              {isAuthenticated && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSaveSearch({})}
                  className="hidden md:flex"
                  data-testid="save-search-btn"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Save Search
                </Button>
              )}

              {/* View Toggle */}
              <div className="flex border rounded-md">
                <button
                  onClick={() => setViewMode('grid')}
                  className={cn(
                    "p-2 transition-colors",
                    viewMode === 'grid' ? "bg-blue-50 text-blue-600" : "hover:bg-gray-50"
                  )}
                  data-testid="view-grid"
                  title="Grid View"
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={cn(
                    "p-2 transition-colors",
                    viewMode === 'list' ? "bg-blue-50 text-blue-600" : "hover:bg-gray-50"
                  )}
                  data-testid="view-list"
                  title="List View"
                >
                  <List className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={cn(
                    "p-2 transition-colors",
                    viewMode === 'map' ? "bg-blue-50 text-blue-600" : "hover:bg-gray-50"
                  )}
                  data-testid="view-map"
                  title="Map View"
                >
                  <Map className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={cn(
        "mx-auto px-4 py-6",
        viewMode === 'map' ? "max-w-full h-[calc(100vh-180px)]" : "max-w-7xl"
      )}>
        {/* MAP VIEW */}
        {viewMode === 'map' ? (
          <div className="flex gap-4 h-full">
            {/* Filters Sidebar for Map */}
            {showFilters && !isMobile && (
              <div className="w-80 flex-shrink-0">
                <FilterPanel
                  filters={filters}
                  setFilters={setFilters}
                  isOpen={true}
                  onClose={() => setShowFilters(false)}
                  isMobile={false}
                />
              </div>
            )}
            
            {/* Map */}
            <PropertyMapView
              ref={mapRef}
              filters={filters}
              onFiltersChange={setFilters}
              onSaveSearch={handleSaveSearch}
              favorites={favorites}
              onFavorite={handleFavorite}
              isAuthenticated={isAuthenticated}
              className="flex-1 h-full"
            />

            {/* Mobile Filter Panel */}
            {isMobile && (
              <FilterPanel
                filters={filters}
                setFilters={setFilters}
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                isMobile={true}
              />
            )}
          </div>
        ) : (
          /* GRID/LIST VIEW */
          <div className="flex gap-6">
            {/* Filter Panel */}
            <FilterPanel
              filters={filters}
              setFilters={setFilters}
              isOpen={showFilters}
              onClose={() => setShowFilters(false)}
              isMobile={isMobile}
            />

            {/* Results */}
            <div className="flex-1">
              {/* Results Header */}
              <div className="mb-4 flex items-center justify-between">
                <p className="text-gray-600">
                  {loading ? 'Loading...' : `${total} properties found`}
                </p>
              </div>

              {/* Error State */}
              {error && (
                <Card className="p-8 text-center">
                  <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
                  <p className="text-gray-600 mb-4">{error}</p>
                  <Button onClick={() => loadListings(true)}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                </Card>
              )}

              {/* Loading State */}
              {loading && listings.length === 0 && (
                <div className={cn(
                  "gap-6",
                  viewMode === 'grid' 
                    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" 
                    : "space-y-4"
                )}>
                  {[...Array(6)].map((_, i) => (
                    <Card key={i} className="animate-pulse">
                      <div className="h-48 bg-gray-200" />
                      <CardContent className="p-4 space-y-3">
                        <div className="h-4 bg-gray-200 rounded w-3/4" />
                        <div className="h-3 bg-gray-200 rounded w-1/2" />
                        <div className="h-3 bg-gray-200 rounded w-1/4" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
            )}

            {/* Empty State */}
            {!loading && !error && listings.length === 0 && (
              <Card className="p-12 text-center">
                <Home className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No properties found</h3>
                <p className="text-gray-500 mb-6">
                  Try adjusting your filters or search in a different area.
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setFilters({
                      minPrice: '',
                      maxPrice: '',
                      bedrooms: '',
                      propertyType: '',
                      amenities: [],
                    });
                    setSearchQuery('');
                  }}
                >
                  Clear All Filters
                </Button>
              </Card>
            )}

            {/* Results Grid/List */}
            {listings.length > 0 && (
              <>
                <div className={cn(
                  "gap-6",
                  viewMode === 'grid' 
                    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" 
                    : "space-y-4"
                )}>
                  {listings.map((listing) => (
                    <PropertyCard
                      key={listing.id}
                      listing={listing}
                      viewMode={viewMode}
                      onFavorite={handleFavorite}
                      isFavorite={favorites.has(listing.id)}
                    />
                  ))}
                </div>

                {/* Load More */}
                {hasMore && (
                  <div className="mt-8 text-center">
                    <Button
                      variant="outline"
                      onClick={handleLoadMore}
                      disabled={loading}
                      data-testid="load-more"
                    >
                      {loading ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        'Load More Properties'
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Save Search Modal */}
      <SaveSearchModal
        isOpen={showSaveSearchModal}
        onClose={() => setShowSaveSearchModal(false)}
        searchCriteria={pendingSearchCriteria}
        onSave={() => {
          setPendingSearchCriteria(null);
          navigate('/saved-searches');
        }}
      />
    </div>
  );
}
